// "Connection checks" — manual connection capability, the unified fastening
// sheet, and one exact bolt record. Engineering claims remain behind the
// publication blockers called out below; fixture values are shared with the
// capture harness.
import type { HelpArticle } from "../../types";
import {
  CONNECTION_BOLT_DEMO,
  MAPLE_PROJECT,
} from "../../../calculations/calcPreviewFixtures";

const lb = (value: number): string => value.toLocaleString("en-US", {
  minimumFractionDigits: Number.isInteger(value) ? 0 : 1,
  maximumFractionDigits: 1,
});

export const connectionChecksArticle: HelpArticle = {
  id: "connection-checks",
  title: "Connection checks",
  description:
    "Choose the manual connection model, declare capacity and demand, and read the engine record without losing the sheet when the fastening changes.",
  tags: ["connection", "bolts", "nails", "screws", "lags", "hanger", "hold-down"],
  lastUpdated: "July 20, 2026",
  published: false,
  blocks: [
    {
      kind: "lead",
      text: "Manual mode has seven connection calculators. Five share one sheet and one Fastening master control; proprietary hanger and hold-down checks remain separate because they consume installation-specific product records. The common workflow is capacity first, demand second, then an engine-authored check record.",
    },

    { kind: "heading", level: 2, text: "Start the right connection record", id: "start" },
    { kind: "where", path: ["Calculations", MAPLE_PROJECT.name, "Add calculation", "Connections"] },
    {
      kind: "paragraph",
      text: "**Connection check** starts on Nails. Its Fastening control switches the same sheet among Nails, Screws, Lags, Bolts, and Hardware (catalog product). The first four use entered fastener, member, group, condition, and load data to produce an engine capacity record. Hardware requires an exact published capacity row before it can produce a verdict. The sheet id survives a switch, so the record is not discarded just because the capacity model changes.",
    },
    {
      kind: "paragraph",
      text: "**Proprietary Hanger Capacity Check** and **Proprietary Hold-Down Load Path** are standalone. They start from catalog installation identities rather than the unified dowel-fastener model. Keep them separate from a hardware reference attached to a nail or screw sheet: an attached reference is identification only, while these calculators are intended to run product-specific checks when their required condition data is available.",
    },
    {
      kind: "shot",
      shotId: "connection-family-catalog",
      caption: "The manual connection family: one five-model sheet, plus standalone hanger and hold-down calculators.",
    },

    { kind: "heading", level: 2, text: "Fastening models and preserved inputs", id: "fastening-models" },
    {
      kind: "steps",
      steps: [
        {
          title: "Name the connection",
          body: "Use a stable mark. The mark remains on the sheet as the fastening model changes and becomes the audit handle in the calculation register and export.",
        },
        {
          title: "Choose the capacity model",
          body: "Select Nails, Screws, Lags, Bolts, or Hardware in the header. Fastener-specific fields reseed from the target calculator; shared connection groups carry across only where the target schema has the same keys.",
        },
        {
          title: "Complete the working groups",
          body: "Enter the fastener or product, member properties, group arrangement, conditions, and the load components exposed by that calculator. Collapsed cards retain their values and summarize them in the card header.",
        },
        {
          title: "Recalculate and inspect the record",
          body: "A field commit saves and recomputes. Read the verdict together with the governing row, reference data, factor ledger, warnings, and calculation basis—not the percentage alone.",
        },
      ],
    },
    {
      kind: "paragraph",
      text: `The ${CONNECTION_BOLT_DEMO.mark} fixture makes those groups concrete: four ${CONNECTION_BOLT_DEMO.diameterIn}-inch bolts in single shear, one row at ${CONNECTION_BOLT_DEMO.spacingIn} inches, and Douglas Fir-Larch members ${CONNECTION_BOLT_DEMO.mainThicknessIn} inches thick on both sides. The group fields carry main-member E = ${CONNECTION_BOLT_DEMO.emPsi.toLocaleString()} psi plus main and side areas of ${CONNECTION_BOLT_DEMO.mainAreaIn2} in². The adapter uses that main E for the wood side when no separate side-member E value exists. These are fixture inputs, not defaults for another connection.`,
    },
    {
      kind: "shot",
      shotId: "connection-bolt-inputs",
      caption: `${CONNECTION_BOLT_DEMO.mark}'s entered fastener, members, group arrangement, and load components.` ,
    },

    { kind: "heading", level: 2, text: "Capacity first, demand second", id: "capacity-and-demand" },
    {
      kind: "paragraph",
      text: "Step 1 names the capacity authority. A dowel sheet summarizes the entered fastening whose capacity the engine computes; a Hardware sheet requires a selected published capacity row. Step 2 names the demand authority. Manual mode reads the fields below, while a reaction link reads one source support live, disables the manual fields but retains their saved values. Removing the link restores the saved manual values.",
    },
    {
      kind: "paragraph",
      text: `For ${CONNECTION_BOLT_DEMO.mark}, the visible components are D = ${lb(CONNECTION_BOLT_DEMO.loadComponentsLb.D)} lb and S = ${lb(CONNECTION_BOLT_DEMO.loadComponentsLb.S)} lb. The engine reports ${CONNECTION_BOLT_DEMO.governingCombination} at ${lb(CONNECTION_BOLT_DEMO.demandLb)} lb as the governing row. This article does not assign service-level or factored meaning to those dowel fields: the current registry labels and the engine's combination behavior disagree, so that basis must be resolved before publication.`,
    },
    {
      kind: "shot",
      shotId: "connection-capacity-demand",
      caption: "The shared setup card declares the capacity model, demand source, and current manual-or-linked state.",
    },
    {
      kind: "paragraph",
      text: "The status chip is workflow state, not a structural verdict. **Setup required** means a Hardware sheet still lacks its exact product capacity. **Manual demand** means the capacity authority is ready and the visible demand fields control the next calculation. **Reaction linked** means a project edge supplies the demand, and the source must remain current. A compute refusal or stale source is not converted into a failing D/C ratio; resolve the missing authority and recalculate.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Publication blocker: dowel demand basis",
      body: "The engine combines entered D/L/Lr/S components, while several registry help strings call the same fields factored demands. Do not convert, factor, or relabel project forces from this article. Founder and PE review must establish one input contract first.",
    },

    { kind: "heading", level: 2, text: `Read the ${CONNECTION_BOLT_DEMO.mark} result`, id: "computed-capacity" },
    {
      kind: "values",
      caption: "Exact values produced by the shared bolt verification fixture.",
      rows: [
        { label: "Governing row", value: `${CONNECTION_BOLT_DEMO.governingCombination} · ${lb(CONNECTION_BOLT_DEMO.demandLb)} lb` },
        { label: "Capacity", value: `${lb(CONNECTION_BOLT_DEMO.capacityLb)} lb` },
        { label: "Utilization", value: `${(CONNECTION_BOLT_DEMO.dcr * 100).toFixed(1)}% · Pass` },
        { label: "Reference value", value: `Z = ${CONNECTION_BOLT_DEMO.zRefLbPerFastener.toFixed(1)} lb / fastener · mode ${CONNECTION_BOLT_DEMO.governingYieldMode}` },
        { label: "Reported factors", value: `CD ${CONNECTION_BOLT_DEMO.loadDurationFactor.toFixed(3)} · Cg ${CONNECTION_BOLT_DEMO.groupActionFactor.toFixed(3)}` },
      ],
    },
    {
      kind: "paragraph",
      text: "The Design summary gives the decision and governing demand/capacity pair. Fastener & member reference repeats the diameter, Fyb, count, shear planes, member properties, and all candidate yield-mode values used by the record. Adjustment factors then name each applied factor and its basis. The warning is part of the answer: this fixture used CΔ = 1.0, while bolt end distance, edge distance, and spacing minimums were not automatically checked.",
    },
    {
      kind: "shot",
      shotId: "connection-bolt-results",
      caption: `${CONNECTION_BOLT_DEMO.mark}: verdict, engine reference, yield modes, and adjustment-factor ledger in one trace.` ,
    },
    {
      kind: "callout",
      variant: "note",
      title: "PE review required before publication",
      body: "Descriptions of yield-limit behavior, Cg, CΔ, load-to-grain effects, side-member scope, and the absence of a bolt withdrawal check require PE ratification. The numeric fixture is test-traceable; its engineering interpretation is not silently promoted here.",
    },
    {
      kind: "callout",
      variant: "tip",
      title: "A failed fastening is still a record",
      body: "If a dowel check fails, keep its verdict and follow the hardware escalation workflow. The separate escalation article covers the paired sheets, carried reaction link, and printed cross-notes; this article does not duplicate that decision trail.",
    },

    { kind: "related", topics: ["fastening-escalation", "linking-reactions", "product-lookup"] },
  ],
};
